/**
 * Página de Categorías — ListView + useListViews + useStandardListFilters.
 * Misma estructura que Etiquetas; solo cambian datos y config.
 */

import { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BlockStack,
  Banner,
  ChoiceList,
  Modal,
  Text,
  IndexFiltersMode,
  useSetIndexFiltersMode,
} from '@/lib/polaris';
import { supabase } from '@/lib/supabase';
import { fetchAllProductRows } from '@/lib/productsApi';
import { useAuth, usePagination } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { routeHelpers, storePath, slugifyProductName } from '@/config/routes.config';
import { exportListToExcel, getExportFilename } from '@/lib/exportToExcel';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ListView, buildListViewPagination } from '@/components/admin/ListView';
import { getCategoriesListConfig } from '@/config/listViews';
import { getStatsPageCache, setStatsPageCache, listViewCacheKey } from '@/lib/statsPageCache';
import type { CategoryListItem } from '@/config/listViews';
import { filterBySearchName, filterByWithWithoutProducts, sortByNameOrProducts } from '@/lib/listViewFilters';
import { useListViews } from '@/hooks/useListViews';
import { useCanManage } from '@/hooks/useCanManage';
import { useStandardListFilters } from '@/hooks/useStandardListFilters';
import { ProductIcon } from '@/lib/polaris-icons';

const LIST_ID = 'categories';

const CATEGORIES_SYSTEM_VIEWS = [
  {
    id: 'todos',
    label: 'Todos',
    isSystem: true,
    query: '',
    filterValues: { status: [] as string[], products: [] as string[] },
  },
];
const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Nombre A-Z' },
  { value: 'name_desc', label: 'Nombre Z-A' },
  { value: 'products_desc', label: 'Más productos primero' },
  { value: 'products_asc', label: 'Menos productos primero' },
] as const;

const SORT_OPTIONS_POLARIS = SORT_OPTIONS.map((o) => ({
  label: o.label,
  value: o.value.replace('_', ' ') as `${string} ${'asc' | 'desc'}`,
  directionLabel: o.label,
}));

const STATUS_FILTER_OPTIONS = [
  { label: 'Activa', value: 'active' },
  { label: 'Inactiva', value: 'inactive' },
] as const;

function filterCategoriesByStatus(
  items: CategoryListItem[],
  status: string[]
): CategoryListItem[] {
  if (status.length === 0) return items;
  return items.filter((c) => {
    const active = c.is_active;
    if (status.includes('active') && status.includes('inactive')) return true;
    if (status.includes('active')) return active;
    if (status.includes('inactive')) return !active;
    return true;
  });
}

export default function CategoriesPage() {
  const { storeSlug } = useParams<{ storeSlug?: string }>();
  const { tenantId } = useAuth();
  const canManage = useCanManage();
  const navigate = useNavigate();
  const toast = useAdminToast();

  const [categories, setCategories] = useState<CategoryListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const [searchValue, setSearchValue] = useState('');
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterProducts, setFilterProducts] = useState<string[]>([]);
  const [sortOption, setSortOption] = useState<string>(SORT_OPTIONS[0].value);
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Default);

  const filtersRecord = useMemo(
    () => ({ status: filterStatus, products: filterProducts }),
    [filterStatus, filterProducts]
  );
  const setFiltersFromView = useCallback((f: Record<string, string[]>) => {
    setFilterStatus(f.status ?? []);
    setFilterProducts(f.products ?? []);
  }, []);

  const views = useListViews({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    systemViews: CATEGORIES_SYSTEM_VIEWS,
    searchValue,
    filters: filtersRecord,
    setSearchValue,
    setFilters: setFiltersFromView,
    setMode,
    mode,
  });
  const { activeView, handleSwitchView } = views;

  const toUrl = useCallback(
    (path: string) => navigate(storeSlug ? storePath(storeSlug, path) : path),
    [navigate, storeSlug]
  );
  const config = getCategoriesListConfig(toUrl);

  const loadCategories = useCallback(async (revalidateOnly = false) => {
    if (!tenantId) return;
    const cacheKey = listViewCacheKey('categories', tenantId);
    if (!revalidateOnly) {
      const cached = getStatsPageCache<{ items: CategoryListItem[] }>(cacheKey);
      if (cached?.items?.length !== undefined) {
        setCategories(cached.items);
        setLoading(false);
        loadCategories(true).catch(() => {});
        return;
      }
    }
    if (!revalidateOnly) setLoading(true);
    try {
      // `products` se pagina: sin eso PostgREST cortaba en 1000 y los product_count
      // sub-contaban en silencio pasando esa marca.
      const [categoriesResult, allProducts] = await Promise.all([
        supabase
          .from('categories')
          .select('id,name,description,image_url,display_order,is_active,tenant_id,slug')
          .eq('tenant_id', tenantId)
          .order('display_order'),
        fetchAllProductRows<{ category: string | null }>(tenantId, 'category'),
      ]);

      if (categoriesResult.error) throw categoriesResult.error;

      const productCounts = allProducts.reduce(
        (acc, product) => {
          if (product.category) {
            acc[product.category] = (acc[product.category] || 0) + 1;
          }
          return acc;
        },
        {} as Record<string, number>
      );

      const categoriesWithCounts = (categoriesResult.data || []).map(
        (category: { slug?: string; name: string; [k: string]: unknown }) => ({
          ...category,
          icon: '',
          product_count: productCounts[category.name] || 0,
          slug: category.slug ?? slugifyProductName(category.name),
        })
      ) as CategoryListItem[];

      setCategories(categoriesWithCounts);
      setStatsPageCache(cacheKey, { items: categoriesWithCounts });
    } catch (error: unknown) {
      toast.error(
        'Error al cargar categorías',
        error instanceof Error ? error.message : 'Error desconocido'
      );
    } finally {
      setLoading(false);
    }
  }, [tenantId, toast]);

  useLayoutEffect(() => {
    if (tenantId) loadCategories();
  }, [tenantId, loadCategories]);

  const handleDeleteClick = useCallback((category: CategoryListItem) => {
    setCategoryToDelete({ id: category.id, name: category.name });
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!categoryToDelete) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from('categories').delete().eq('id', categoryToDelete.id);
      if (error) throw error;
      toast.success('Categoría eliminada', categoryToDelete.name);
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
      await loadCategories();
    } catch (error: unknown) {
      toast.error(
        'Error al eliminar categoría',
        error instanceof Error ? error.message : 'Error desconocido'
      );
    } finally {
      setDeleting(false);
    }
  }, [categoryToDelete, loadCategories, toast]);

  // Borrado masivo: la plantilla pone la acción destructiva, el modal de confirmación y el toast.
  // La página solo aporta el borrado real (scoped por tenant) y recarga.
  const bulkDeleteCategories = useCallback(async (ids: string[]) => {
    if (!tenantId) return 0;
    const { error } = await supabase.from('categories').delete().in('id', ids).eq('tenant_id', tenantId);
    if (error) throw error;
    await loadCategories();
    return ids.length;
  }, [tenantId, loadCategories]);

  const filteredCategories = useMemo(() => {
    const bySearch = filterBySearchName(categories, searchValue);
    const byStatus = filterCategoriesByStatus(bySearch, filterStatus);
    const byProducts = filterByWithWithoutProducts(byStatus, filterProducts);
    return sortByNameOrProducts(byProducts, sortOption);
  }, [categories, searchValue, filterStatus, filterProducts, sortOption]);

  const pagination = usePagination();
  useEffect(() => {
    pagination.setTotal(filteredCategories.length);
    if (filteredCategories.length > 0 && pagination.offset >= filteredCategories.length) {
      pagination.goToFirstPage();
    }
  }, [filteredCategories.length, pagination]);

  const listItems = useMemo(
    () => filteredCategories.slice(pagination.offset, pagination.offset + pagination.pageSize),
    [filteredCategories, pagination.offset, pagination.pageSize]
  );

  const handleBulkAction = useCallback(
    (actionKey: string, selectedIds: string[]) => {
      if (actionKey === 'export' && selectedIds.length > 0) {
        const items = filteredCategories.filter((c) => selectedIds.includes(c.id));
        exportListToExcel({
          columns: config.columns,
          items: items.map((i) => ({ ...i })),
          filename: getExportFilename(config.title),
        });
        toast.success('Exportado', `${items.length} categoría(s) exportada(s) a Excel`);
      }
    },
    [config.columns, config.title, filteredCategories, toast]
  );

  const paginationOption = buildListViewPagination(pagination);

  const clearFilters = useCallback(() => {
    handleSwitchView(0);
  }, [handleSwitchView]);

  const appliedFilters = useMemo(() => {
    const items: { key: string; label: string; onRemove: () => void }[] = [];
    if (filterStatus.length > 0) {
      const labels = filterStatus
        .map((v) => STATUS_FILTER_OPTIONS.find((o) => o.value === v)?.label ?? v)
        .join(', ');
      items.push({
        key: 'status',
        label: `Estado: ${labels}`,
        onRemove: () => setFilterStatus([]),
      });
    }
    if (filterProducts.length > 0) {
      const labels = filterProducts
        .map((v) => (v === 'with_products' ? 'Con productos' : 'Sin productos'))
        .join(', ');
      items.push({
        key: 'products',
        label: `Productos: ${labels}`,
        onRemove: () => setFilterProducts([]),
      });
    }
    return items;
  }, [filterStatus, filterProducts]);

  const indexFiltersFilters = useMemo(
    () => [
      {
        key: 'status',
        label: 'Estado',
        shortcut: true,
        pinned: true,
        filter: (
          <ChoiceList
            title="Estado"
            titleHidden
            choices={STATUS_FILTER_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
            selected={filterStatus}
            onChange={setFilterStatus}
            allowMultiple
          />
        ),
      },
      {
        key: 'products',
        label: 'Productos',
        shortcut: true,
        filter: (
          <ChoiceList
            title="Productos"
            titleHidden
            choices={[
              { label: 'Con productos', value: 'with_products' },
              { label: 'Sin productos', value: 'without_products' },
            ]}
            selected={filterProducts}
            onChange={setFilterProducts}
            allowMultiple
          />
        ),
      },
    ],
    [filterStatus, filterProducts]
  );

  const {
    filterControl,
    columnOrder: tableColumnOrder,
    hiddenColumnKeys: tableHiddenKeys,
    editColumnsMode,
    editingColumnPrefs,
    onColumnOrderChange,
    onToggleColumnVisibility,
  } = useStandardListFilters({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    config,
    views,
    searchValue,
    setSearchValue,
    queryPlaceholder: activeView ? `Buscando en ${activeView.label}` : 'Buscar categorías',
    filters: indexFiltersFilters,
    appliedFilters,
    onClearAll: clearFilters,
    sortOptions: SORT_OPTIONS_POLARIS,
    sortSelected: [sortOption.replace('_', ' ')],
    onSort: (v) => setSortOption((v[0] ?? sortOption).replace(' ', '_')),
    mode,
    setMode,
  });

  const pageTitleNode = (
    <PageBreadcrumbTitle
      icon={ProductIcon}
      title={config.title}
      onBack={config.onBack}
      backAccessibilityLabel={config.backAccessibilityLabel ?? 'Volver a Productos'}
    />
  );

  return (
    <>
      <ListView<CategoryListItem>
        config={config}
        items={listItems}
        loading={loading}
        pagination={paginationOption}
        loadingMessage="Cargando categorías…"
        pageTitle={pageTitleNode}
        contentBefore={filterControl}
        contentBeforeInsideCard
        selectedIds={selectedCategories}
        onSelectionChange={setSelectedCategories}
        columnOrder={tableColumnOrder}
        hiddenColumnKeys={tableHiddenKeys}
        editColumnsMode={editColumnsMode}
        columnOrderForEdit={editingColumnPrefs?.columnOrder?.length ? editingColumnPrefs.columnOrder : tableColumnOrder}
        onColumnOrderChange={onColumnOrderChange}
        onToggleColumnVisibility={onToggleColumnVisibility}
        onRowEdit={(item) => toUrl(routeHelpers.categoryEdit(item.slug))}
        onRowDelete={handleDeleteClick}
        onBulkAction={handleBulkAction}
        bulkDelete={{ onDelete: bulkDeleteCategories, can: canManage }}
      />

      <Modal
        open={deleteModalOpen}
        onClose={() => {
          if (!deleting) {
            setDeleteModalOpen(false);
            setCategoryToDelete(null);
          }
        }}
        title="Eliminar categoría"
        primaryAction={{
          content: deleting ? 'Eliminando…' : 'Eliminar',
          tone: 'critical',
          onAction: handleDeleteConfirm,
          loading: deleting,
        }}
        secondaryActions={[
          {
            content: 'Cancelar',
            onAction: () => {
              setDeleteModalOpen(false);
              setCategoryToDelete(null);
            },
            disabled: deleting,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Banner tone="critical">
              <BlockStack gap="200">
                <Text as="p">
                  ¿Estás seguro de que deseas eliminar la categoría "{categoryToDelete?.name}"?
                </Text>
                <Text variant="bodySm" tone="subdued" as="p">
                  Los productos asociados no se eliminarán, pero perderán su categoría.
                </Text>
              </BlockStack>
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>

    </>
  );
}
